import { AlertTriangle, Download, FileAudio } from 'lucide-react'
import type { SyntheticEvent } from 'react'
import type { FileLabel } from '@/components/project/analysis-modal/types'
import { FileLabelSelector } from '@/components/project/FileLabelSelector'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { useTranslation } from '@/hooks/useTranslation'

type TFunc = ReturnType<typeof useTranslation>['t']

export interface AudioRecorderDialogPreviewStepProps {
	t: TFunc
	previewUrl: string | null
	filename: string
	onFilenameChange: (value: string) => void
	classification: FileLabel
	onClassificationChange: (value: FileLabel) => void
	isOnline: boolean
	persistReady: boolean
	onSave: () => void
	onDownload: () => void
	onRecordAgain: () => void
	onAudioLoadedMetadata: (e: SyntheticEvent<HTMLAudioElement>) => void
}

export function AudioRecorderDialogPreviewStep({
	t,
	previewUrl,
	filename,
	onFilenameChange,
	classification,
	onClassificationChange,
	isOnline,
	persistReady,
	onSave,
	onDownload,
	onRecordAgain,
	onAudioLoadedMetadata,
}: AudioRecorderDialogPreviewStepProps) {
	return (
		<>
			<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-1">
				{/* biome-ignore lint/a11y/useMediaCaption: preview only */}
				<audio
					src={previewUrl ?? undefined}
					controls
					className="w-full"
					preload="metadata"
					onLoadedMetadata={onAudioLoadedMetadata}
				/>
				<div className="space-y-2">
					<Label htmlFor="audio-filename" className="flex items-center gap-2 font-medium">
						<FileAudio className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						{t('pages.projectDetail.audioRecorder.filenameLabel')}
					</Label>

					<Input
						id="audio-filename"
						value={filename}
						onChange={(e) => onFilenameChange(e.target.value)}
						className="font-medium"
					/>
				</div>

				<FileLabelSelector
					label={classification}
					onChange={onClassificationChange}
					isContextFile={false}
					variant="plain"
				/>
			</div>

			<div className="flex shrink-0 flex-col gap-2">
				<div className="flex gap-2">
					<Button
						type="button"
						variant="outline"
						className={isOnline ? 'flex-1' : 'w-full'}
						onClick={onRecordAgain}
					>
						{t('pages.projectDetail.audioRecorder.recordAgain')}
					</Button>

					{isOnline ? (
						<Button
							type="button"
							variant="outline"
							size="icon"
							onClick={onDownload}
							aria-label={t('pages.projectDetail.audioRecorder.download.button')}
							title={t('pages.projectDetail.audioRecorder.download.button')}
						>
							<Download className="size-4" aria-hidden />
						</Button>
					) : null}
				</div>

				{!isOnline ? (
					<div
						role="status"
						className="flex flex-col gap-3 rounded-md border border-purple-200 bg-purple-50 px-3 py-3 text-sm text-purple-900 dark:border-purple-800/50 dark:bg-purple-950/30 dark:text-purple-200"
					>
						<div className="flex items-start gap-2">
							<AlertTriangle
								className="mt-0.5 size-4 shrink-0 text-purple-600 dark:text-purple-300"
								aria-hidden
							/>
							<span>{t('pages.projectDetail.audioRecorder.offlineWarning')}</span>
						</div>
						<p className="text-xs text-purple-800/90 dark:text-purple-200/90">
							{t('pages.projectDetail.audioRecorder.offlineSaveDisabledHint')}
						</p>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="w-full gap-2 border-purple-300 bg-white/80 text-purple-900 hover:bg-white dark:border-purple-700 dark:bg-purple-950/40 dark:text-purple-100 dark:hover:bg-purple-900/50"
							onClick={onDownload}
						>
							<Download className="size-4 shrink-0" aria-hidden />
							{t('pages.projectDetail.audioRecorder.download.button')}
						</Button>
					</div>
				) : null}

				{isOnline ? (
					<Button
						type="button"
						className="w-full bg-brand-accent text-white hover:bg-brand-accent-hover"
						onClick={() => onSave()}
						disabled={!filename.trim() || !classification.timeDimension || !persistReady}
					>
						{t('pages.projectDetail.audioRecorder.save')}
					</Button>
				) : null}
			</div>
		</>
	)
}
